import { IWidgetType, getWidgetName } from '@framework/engine';
import { yup } from '@framework/validation';
import { fromPairs } from 'lodash';
import { IMultiPreferenceWidget } from './IMultiPreferenceWidget';
import { computeMultiPreferenceTransformId } from './computeMultiPreferenceTransforms';

export const multiPreferenceFieldKey = (index: number): `field${number}` =>
  `field${index}`;

export interface IMultiPreferenceRow {
  id?: string;
  [key: `field${number}`]: string;
}

/**
 * Il widget multi preference è un array di oggetti, dove ognuno
 * rappresenta una riga, ovvero una preferenza selezionata:
 * Una riga è composta dalle scelte fatte dall'utente per ogni colonna
 * del cascade.
 */
export const createMultiPreferenceFormSchema: IWidgetType<IMultiPreferenceWidget>['createFormSchema'] =
  ({ widget, section, page, parentScope }) => {
    const fieldName = getWidgetName(widget);

    const PreferenceRowSchema = yup.object({
      /**
       * Colonna che contiene il valore univoco della riga, che viene
       * poi esportato al Backend tramite metadati.
       * @see packages/common-web/src/engine/directives/metadata/exportMetadataDirective.ts
       */
      id: yup.string().when(
        // Per ogni colonna del cascade.
        widget.cascade?.fields.map(field =>
          multiPreferenceFieldKey(field.index),
        ) ?? [],
        ([...values], schema) => {
          // Se non ci sono colonne, non serve il campo id
          return schema.transform(() =>
            computeMultiPreferenceTransformId(values),
          );
        },
      ),
      /**
       * Per ogni colonna del cascade viene creato un campo con nome
       * `fieldX` dove registriamo il valore della colonna n. X
       */
      ...fromPairs(
        widget.cascade?.fields.map(field => [
          multiPreferenceFieldKey(field.index),
          yup.string().required().label(field.label),
        ]) ?? [],
      ),
    });

    return {
      [fieldName]: yup.array(PreferenceRowSchema).default([]),
    };
  };
